/**
 * Zustand slice for the AI Assistant page.
 *
 * Holds the conversation threads, the streaming cursor, the workspace snapshot
 * used for grounded answers, and the UI chrome flags. Persistence to
 * localStorage happens on every thread mutation via `persist()` below, so a
 * refresh or an accidental navigation does not lose the conversation.
 *
 * Threads are ordered most-recently-updated first; the sidebar renders that
 * order directly.
 */
import { create } from 'zustand';
import {
  deriveTitle,
  loadFlag,
  loadThreads,
  newId,
  saveFlag,
  saveThreads,
} from '../utils/threadStorage';

const makeThread = (title = '') => ({
  id: newId(),
  title,
  createdAt: Date.now(),
  updatedAt: Date.now(),
  messages: [],
});

/**
 * Sort newest-first and write through to storage.
 *
 * Every reducer that touches `threads` returns through here, so there is exactly
 * one place that can forget to save.
 */
const persist = (threads) => {
  const ordered = [...threads].sort(
    (a, b) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0),
  );
  saveThreads(ordered);
  return ordered;
};

const initialThreads = loadThreads();

const useAssistantStore = create((set, get) => ({
  // ---- conversations -------------------------------------------------------
  threads: initialThreads,
  activeThreadId: initialThreads[0]?.id || null,

  /** Per-thread composer text, so switching threads does not lose a draft. */
  drafts: {},

  /**
   * The dataset staged in the composer, keyed by thread id.
   *
   * Keyed by thread for the same reason `drafts` is: `AssistantThread` is
   * remounted by `key={activeThreadId}` but the COMPOSER is not, so composer
   * component state would carry a staged zip into the next conversation.
   *
   * These are DESCRIPTORS only — counts, names, an id into `datasetVault`.
   * Never a File: this slice is not persisted, but a descriptor that reached a
   * message would be, and a File serialises to `{}`.
   */
  attachments: {},

  /**
   * Live progress of a running import, keyed by the card's message id.
   *
   * Deliberately NOT on the message. Every thread mutation re-sorts and
   * re-creates the whole thread array and schedules a localStorage write, so
   * per-image progress ticking through a message would churn both continuously.
   * Only step TRANSITIONS touch the card itself.
   */
  runs: {},

  // ---- request lifecycle ---------------------------------------------------
  /** { threadId, messageId } while a reply is streaming, else null. */
  streaming: null,
  /** Page-level error banner text (transport failures), else null. */
  error: null,

  // ---- grounding -----------------------------------------------------------
  /** Send the user's real projects/models with each question. */
  includeContext: loadFlag('include-context', true),
  /** { projects, totals } | null — drives the context panel and the chips. */
  workspace: null,
  workspaceLoading: false,
  /** Backend capability report from GET /ai_assistant/status. */
  status: null,

  // ---- chrome --------------------------------------------------------------
  sidebarOpen: false, // mobile drawer; desktop uses sidebarCollapsed
  sidebarCollapsed: loadFlag('sidebar-collapsed', false),
  search: '',
  shortcutsOpen: false,
  contextPanelOpen: false,
  /** id of the message being edited in place, else null. */
  editingMessageId: null,

  // =========================================================================
  // Thread actions
  // =========================================================================

  /**
   * Start a new conversation.
   *
   * Reuses the active thread when it is already empty, so hammering "New chat"
   * cannot pile up blank rows in the sidebar.
   */
  newThread: () => {
    const { threads, activeThreadId } = get();
    const active = threads.find((t) => t.id === activeThreadId);
    if (active && active.messages.length === 0) {
      set({ sidebarOpen: false, error: null, editingMessageId: null });
      return active.id;
    }
    const thread = makeThread();
    set({
      threads: persist([thread, ...threads]),
      activeThreadId: thread.id,
      sidebarOpen: false,
      error: null,
      editingMessageId: null,
    });
    return thread.id;
  },

  /** Ensure a thread exists to write into, returning its id. */
  ensureThread: () => {
    const { activeThreadId, threads } = get();
    if (activeThreadId && threads.some((t) => t.id === activeThreadId)) return activeThreadId;
    const thread = makeThread();
    set({ threads: persist([thread, ...threads]), activeThreadId: thread.id });
    return thread.id;
  },

  selectThread: (id) =>
    set({ activeThreadId: id, sidebarOpen: false, error: null, editingMessageId: null }),

  renameThread: (id, title) =>
    set((state) => ({
      threads: persist(
        state.threads.map((thread) =>
          thread.id === id
            ? { ...thread, title: (title || '').trim() || thread.title, updatedAt: Date.now() }
            : thread,
        ),
      ),
    })),

  deleteThread: (id) =>
    set((state) => {
      const threads = persist(state.threads.filter((thread) => thread.id !== id));
      const attachments = { ...state.attachments };
      delete attachments[id];
      return {
        threads,
        // Deleting the open thread falls back to the newest remaining one.
        activeThreadId: state.activeThreadId === id ? threads[0]?.id || null : state.activeThreadId,
        editingMessageId: null,
        attachments,
      };
    }),

  deleteAllThreads: () => {
    set({
      threads: persist([]),
      activeThreadId: null,
      editingMessageId: null,
      drafts: {},
      attachments: {},
      runs: {},
    });
  },

  // =========================================================================
  // Message actions
  // =========================================================================

  /**
   * Append a message and return its id.
   *
   * Also titles the thread from its first user message, which is why titling
   * lives here rather than in the hook — every send path goes through it.
   */
  appendMessage: (threadId, message) => {
    const id = message.id || newId();
    const entry = { id, createdAt: Date.now(), ...message };
    set((state) => ({
      threads: persist(
        state.threads.map((thread) => {
          if (thread.id !== threadId) return thread;
          const isFirstUserMessage =
            entry.role === 'user' && !thread.messages.some((m) => m.role === 'user');
          return {
            ...thread,
            title: isFirstUserMessage ? deriveTitle(entry.content, thread.title) : thread.title,
            messages: [...thread.messages, entry],
            updatedAt: Date.now(),
          };
        }),
      ),
    }));
    return id;
  },

  /** Patch one message in place (streaming text, error flag, engine badge). */
  updateMessage: (threadId, messageId, patch) =>
    set((state) => ({
      threads: persist(
        state.threads.map((thread) =>
          thread.id !== threadId
            ? thread
            : {
                ...thread,
                messages: thread.messages.map((message) =>
                  message.id === messageId ? { ...message, ...patch } : message,
                ),
                updatedAt: Date.now(),
              },
        ),
      ),
    })),

  /**
   * Drop `messageId` and everything after it.
   *
   * Backs both Regenerate (drop the answer, re-ask) and Edit (drop the question
   * and its answer, then re-send the edited text).
   */
  truncateFrom: (threadId, messageId) =>
    set((state) => ({
      threads: persist(
        state.threads.map((thread) => {
          if (thread.id !== threadId) return thread;
          const index = thread.messages.findIndex((m) => m.id === messageId);
          if (index < 0) return thread;
          return { ...thread, messages: thread.messages.slice(0, index), updatedAt: Date.now() };
        }),
      ),
    })),

  // =========================================================================
  // UI / request state
  // =========================================================================

  setStreaming: (value) => set({ streaming: value }),
  setError: (value) => set({ error: value || null }),

  setDraft: (threadId, text) =>
    set((state) => ({ drafts: { ...state.drafts, [threadId]: text } })),

  // =========================================================================
  // Dataset attachment + build cards
  // =========================================================================

  setAttachment: (threadId, descriptor) =>
    set((state) => ({ attachments: { ...state.attachments, [threadId]: descriptor } })),

  clearAttachment: (threadId) =>
    set((state) => {
      if (!(threadId in state.attachments)) return {};
      const attachments = { ...state.attachments };
      delete attachments[threadId];
      return { attachments };
    }),

  /**
   * Patch a build card, merging `plan` and `run` ONE LEVEL DEEP.
   *
   * `updateMessage` is a shallow merge, so patching a card with
   * `{ plan: { classes } }` through it would replace the entire plan object and
   * silently drop `target`, `grouping`, `limits` and `source` — the card would
   * still render, with the project name gone.
   */
  patchBuildCard: (threadId, messageId, patch) =>
    set((state) => ({
      threads: persist(
        state.threads.map((thread) =>
          thread.id !== threadId
            ? thread
            : {
                ...thread,
                messages: thread.messages.map((message) => {
                  if (message.id !== messageId) return message;
                  const next = { ...message, ...patch };
                  if (patch.plan) next.plan = { ...(message.plan || {}), ...patch.plan };
                  if (patch.run) next.run = { ...(message.run || {}), ...patch.run };
                  return next;
                }),
                updatedAt: Date.now(),
              },
        ),
      ),
    })),

  /** Start (or reset) the live progress for one card. */
  startRun: (messageId, initial) =>
    set((state) => ({
      runs: { ...state.runs, [messageId]: { steps: {}, percent: 0, ...(initial || {}) } },
    })),

  /** Merge into the live progress, including one level into `steps`. */
  patchRun: (messageId, patch) =>
    set((state) => {
      const current = state.runs[messageId] || { steps: {}, percent: 0 };
      const next = { ...current, ...patch };
      if (patch && patch.steps) next.steps = { ...(current.steps || {}), ...patch.steps };
      return { runs: { ...state.runs, [messageId]: next } };
    }),

  clearRun: (messageId) =>
    set((state) => {
      if (!(messageId in state.runs)) return {};
      const runs = { ...state.runs };
      delete runs[messageId];
      return { runs };
    }),

  setIncludeContext: (value) => {
    saveFlag('include-context', value);
    set({ includeContext: !!value });
  },

  setWorkspace: (value) => set({ workspace: value || null }),
  setWorkspaceLoading: (value) => set({ workspaceLoading: !!value }),
  setStatus: (value) => set({ status: value || null }),

  setSearch: (value) => set({ search: value ?? '' }),
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  closeSidebar: () => set({ sidebarOpen: false }),
  toggleSidebarCollapsed: () =>
    set((state) => {
      const next = !state.sidebarCollapsed;
      saveFlag('sidebar-collapsed', next);
      return { sidebarCollapsed: next };
    }),
  setShortcutsOpen: (value) => set({ shortcutsOpen: !!value }),
  setContextPanelOpen: (value) => set({ contextPanelOpen: !!value }),
  setEditingMessageId: (value) => set({ editingMessageId: value || null }),
}));

// ---- selectors -------------------------------------------------------------

/**
 * Both selectors must return a STABLE reference for unchanged state.
 *
 * zustand v5 reads through `useSyncExternalStore`, which compares the selector's
 * result by identity. A selector ending in `|| []` mints a fresh array on every
 * call, so the comparison never matches and the component re-renders forever —
 * hence the shared frozen constant rather than an inline literal. `.find()`
 * returns the stored object itself and `null` is a primitive, so both are safe.
 */
const NO_MESSAGES = Object.freeze([]);

export const selectActiveThread = (state) =>
  state.threads.find((thread) => thread.id === state.activeThreadId) || null;

export const selectActiveMessages = (state) =>
  selectActiveThread(state)?.messages || NO_MESSAGES;

export default useAssistantStore;
